import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Refresh, AlertTriangle } from "@/components/icons";

import { fetchEventLog } from "@/lib/api";
import { HttpError } from "@/lib/http";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableShell } from "@/components/ui/data-table-shell";
import { formatDuration as formatShortDuration, formatEnergy } from "@voltie/localization-web";
import { useCatalog, type Translate } from "@/app/localization";
import { viewerInstant } from "@/app/displayFormat";

/* ---- Event Code Mapping ---- */
// Charger EventLog codes -> event id (catalog select value) and category.
const EVENT_CODES: Record<number, { event: string; category: string }> = {
  // Fatal
  1: { event: "fatal_error", category: "fatal" },
  2: { event: "watchdog_reset", category: "fatal" },
  3: { event: "memory_error", category: "fatal" },
  4: { event: "stack_overflow", category: "fatal" },
  // Error
  10: { event: "overcurrent", category: "error" },
  11: { event: "overvoltage", category: "error" },
  12: { event: "undervoltage", category: "error" },
  13: { event: "ground_fault", category: "error" },
  14: { event: "temperature_high", category: "error" },
  15: { event: "temperature_critical", category: "error" },
  16: { event: "relay_error", category: "error" },
  17: { event: "gfci_fault", category: "error" },
  18: { event: "pilot_error", category: "error" },
  19: { event: "diode_check_fail", category: "error" },
  // Charging
  20: { event: "charging_started", category: "charging" },
  21: { event: "charging_stopped", category: "charging" },
  22: { event: "charging_paused", category: "charging" },
  23: { event: "charging_resumed", category: "charging" },
  24: { event: "session_complete", category: "charging" },
  25: { event: "energy_limit_reached", category: "charging" },
  26: { event: "time_limit_reached", category: "charging" },
  27: { event: "vehicle_disconnected", category: "charging" },
  28: { event: "subsession_start", category: "charging" },
  29: { event: "subsession_end", category: "charging" },
  // Connectivity
  30: { event: "wifi_connected", category: "connectivity" },
  31: { event: "wifi_disconnected", category: "connectivity" },
  32: { event: "server_connected", category: "connectivity" },
  33: { event: "server_disconnected", category: "connectivity" },
  34: { event: "mqtt_connected", category: "connectivity" },
  35: { event: "mqtt_disconnected", category: "connectivity" },
  36: { event: "ota_update_start", category: "connectivity" },
  37: { event: "ota_update_complete", category: "connectivity" },
  38: { event: "ota_update_failed", category: "connectivity" },
  // Misc
  40: { event: "boot", category: "misc" },
  41: { event: "config_changed", category: "misc" },
  42: { event: "firmware_version", category: "misc" },
  43: { event: "time_synced", category: "misc" },
  44: { event: "user_auth", category: "misc" },
  45: { event: "admin_action", category: "misc" },
  // Clear
  50: { event: "error_cleared", category: "clear" },
  51: { event: "fault_reset", category: "clear" },
  52: { event: "all_clear", category: "clear" },
};

const CATEGORY_VARIANT: Record<string, "error" | "warning" | "info" | "primary" | "neutral" | "success"> = {
  fatal: "error",
  error: "warning",
  charging: "info",
  connectivity: "primary",
  misc: "neutral",
  clear: "success",
};

function getEventInfo(code: number): { event: string; category: string } {
  return EVENT_CODES[code] || { event: "other", category: "misc" };
}

function formatDuration(seconds: number | undefined): string {
  if (!seconds) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Stable `error` codes of /api/eventlog's tcp_listener source
// (web/tcp_listener_reader.py), each a case of operator.ops.event_log.source_error.
// `detail` is never shown: it can carry upstream bodies, exception text and internal addresses.
const SOURCE_ERROR_CODES: ReadonlySet<string> = new Set([
  "disabled",
  "unavailable",
  "timeout",
  "unauthorized",
  "invalid_request",
  "upstream_error",
  "bad_response",
]);

function eventLogErrorText(t: Translate, error: unknown): string {
  if (error instanceof HttpError) {
    const code = (error.body as { error?: unknown } | undefined)?.error;
    const status = String(error.status);
    return typeof code === "string" && SOURCE_ERROR_CODES.has(code)
      ? t("operator.ops.event_log.source_error", { code, status })
      : t("operator.ops.event_log.load_failed_status", { status });
  }
  return t("operator.ops.event_log.load_failed");
}

/**
 * tcp_listener rows carry the device's own date and time (`event_date`, `event_time`),
 * shown as sent. Their `timestamp` is only the day label (`2026-10-21Z`): parsed as a
 * Date it is midnight UTC moved into the browser's zone, i.e. the wrong day and no time.
 */
function eventTimeText(row: any): string {
  if (typeof row.event_date === "string" && row.event_date) {
    return typeof row.event_time === "string" && row.event_time
      ? `${row.event_date} ${row.event_time}`
      : row.event_date;
  }
  return row.timestamp ? viewerInstant(row.timestamp, "dateTimeSeconds") ?? "—" : "—";
}

function eventColumns(t: Translate): ColumnDef<any, any>[] {
  return [
  {
    id: "timestamp",
    accessorFn: (row) => row.event_datetime ?? row.event_date ?? row.timestamp,
    header: t("operator.ops.event_log.timestamp"),
    cell: ({ row }) => (
      <span className="font-mono text-xs whitespace-nowrap">{eventTimeText(row.original)}</span>
    ),
  },
  {
    accessorKey: "unit_id",
    header: t("operator.units.column_unit_id"),
    cell: ({ getValue }) => <span className="font-mono text-xs">{getValue() || "—"}</span>,
  },
  {
    accessorKey: "event_code",
    header: t("operator.ops.event_log.code"),
    cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
  },
  {
    id: "event",
    header: t("operator.ops.event_log.event"),
    cell: ({ row }) => {
      const info = getEventInfo(row.original.event_code);
      return row.original.event_name || t("operator.ops.event_log.event_name", { event: info.event, code: String(row.original.event_code) });
    },
  },
  {
    id: "category",
    header: t("operator.ops.event_log.category"),
    cell: ({ row }) => {
      const info = getEventInfo(row.original.event_code);
      return (
        <Badge variant={CATEGORY_VARIANT[info.category] ?? "neutral"} className="text-[11px] h-4.5 px-1.5">
          {t("operator.ops.event_log.category_name", { category: info.category })}
        </Badge>
      );
    },
  },
  {
    accessorKey: "energy_wh",
    header: t("operator.ops.event_log.energy"),
    cell: ({ getValue }) => {
      const v = getValue();
      if (v == null) return "—";
      return Number.isFinite(Number(v)) ? formatEnergy(Number(v), "Wh", "summary") : String(v);
    },
  },
  {
    accessorKey: "duration_seconds",
    header: t("operator.ops.event_log.duration"),
    cell: ({ getValue }) => formatDuration(getValue()),
  },
  ];
}

export function EventLogPage() {
  const { t, catalog } = useCatalog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => eventColumns(t), [catalog]);
  const [unitId, setUnitId] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const q = useQuery({
    queryKey: ["eventlog", { unitId, startTime, endTime }],
    queryFn: () => fetchEventLog({
      unit_id: unitId || undefined,
      start_time: startTime || undefined,
      end_time: endTime || undefined,
    }),
    refetchInterval: autoRefresh ? 10_000 : false,
  });

  // Machine field of the legacy source's 200 answer (web_server.api_eventlog). A healthy
  // answer never carries `error`, so one without a known reason (an older server, or a
  // reason this bundle does not know) is a failure, not an empty log; its text is not shown.
  const isTcpDisabled = q.data?.reason === "tcp_data_server_disabled";
  const isUnexplainedFailure = !isTcpDisabled && !Array.isArray(q.data) && q.data?.error != null && q.data.error !== "";
  const events: any[] = Array.isArray(q.data) ? q.data : q.data?.events || q.data?.event_log || [];

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.nav.event_log")}</h1>
          <div className="text-sm opacity-70">{t("operator.ops.event_log.subtitle")}</div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {t("operator.ops.auto_toggle", { state: autoRefresh ? "on" : "off" })}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => q.refetch()}>
            <Refresh className="h-4 w-4" /> {t("common.action.refresh")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[200px]">
              <Label>{t("operator.units.column_unit_id")}</Label>
              <Input className="mt-1" placeholder={t("operator.ops.event_log.unit_placeholder")} value={unitId} onChange={(e) => setUnitId(e.target.value)} />
            </div>
            <div>
              <Label>{t("operator.ops.event_log.start_time")}</Label>
              <input
                type="datetime-local"
                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 mt-1"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div>
              <Label>{t("operator.ops.event_log.end_time")}</Label>
              <input
                type="datetime-local"
                className="h-8 w-full rounded-4xl border border-input bg-input/30 px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 mt-1"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {isTcpDisabled ? (
        <Alert>
          <AlertTriangle />
          <AlertDescription>{t("operator.ops.event_log.tcp_disabled")}</AlertDescription>
        </Alert>
      ) : q.isError || isUnexplainedFailure ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>{q.isError ? eventLogErrorText(t, q.error) : t("operator.ops.event_log.load_failed")}</AlertDescription>
        </Alert>
      ) : (
        <DataTableShell>
          <DataTable
            columns={columns}
            data={events}
            isLoading={q.isLoading}
            tableClassName="text-xs"
            emptyMessage={t("operator.ops.event_log.empty")}
          />
        </DataTableShell>
      )}

      <div className="text-center text-xs opacity-50">
        {t("operator.ops.event_log.footer", { count: events.length, auto: autoRefresh ? "on" : "off", interval: formatShortDuration(10, "seconds") })}
      </div>
    </div>
  );
}
